import React, { useCallback } from 'react';
import { BubbleMenu } from '@tiptap/react';
import { Bold, Italic, Strikethrough, Underline as UnderlineIcon, Code, type LucideIcon } from 'lucide-react';
import { isTextSelection, type Editor } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { LinkToolbarPopover } from './LinkToolbarPopover';
import styles from './RichTextBubbleMenu.module.css';

type BubbleMark = {
  mark: string;
  label: string;
  Icon: LucideIcon;
  apply: (chain: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>;
};

const BUBBLE_MARKS: BubbleMark[] = [
  { mark: 'bold', label: 'Bold', Icon: Bold, apply: c => c.toggleBold() },
  { mark: 'italic', label: 'Italic', Icon: Italic, apply: c => c.toggleItalic() },
  { mark: 'underline', label: 'Underline', Icon: UnderlineIcon, apply: c => c.toggleUnderline() },
  { mark: 'strike', label: 'Strikethrough', Icon: Strikethrough, apply: c => c.toggleStrike() }
];

export const RichTextBubbleMenu = ({
  editor,
  showMarkdownTextareaRef,
  appendToRef
}: {
  editor: Editor;
  showMarkdownTextareaRef: { current: boolean };
  appendToRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const shouldShow = useCallback(
    ({
      editor: menuEditor,
      view,
      state,
      from,
      to,
      element
    }: {
      editor: Editor;
      view: EditorView;
      state: EditorState;
      from: number;
      to: number;
      element: HTMLElement;
    }) => {
      if (showMarkdownTextareaRef.current) return false;

      const { doc, selection } = state;
      const { empty } = selection;
      const isEmptyTextBlock = !doc.textBetween(from, to).length && isTextSelection(state.selection);
      const isChildOfMenu = element.contains(document.activeElement);
      const hasEditorFocus = view.hasFocus() || isChildOfMenu;

      if (!hasEditorFocus || empty || isEmptyTextBlock || !menuEditor.isEditable) {
        return false;
      }

      return true;
    },
    [showMarkdownTextareaRef]
  );

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={shouldShow}
      tippyOptions={{
        duration: 100,
        appendTo: () => appendToRef.current ?? document.body
      }}
    >
      <div className={styles.bubbleMenu}>
        {BUBBLE_MARKS.map(({ mark, label, Icon, apply }) => (
          <button
            key={mark}
            type="button"
            className={`${styles.bubbleButton} ${editor.isActive(mark) ? styles.bubbleActive : ''}`}
            onMouseDown={e => { e.preventDefault(); apply(editor.chain().focus()).run(); }}
            title={label}
          >
            <Icon size={13} />
          </button>
        ))}
        <LinkToolbarPopover variant="bubble" />
        <span className={styles.bubbleSeparator} />
        <button
          type="button"
          className={`${styles.bubbleButton} ${editor.isActive('code') ? styles.bubbleActive : ''}`}
          onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleCode().run(); }}
          title="Inline code"
        >
          <Code size={13} />
        </button>
      </div>
    </BubbleMenu>
  );
};
